import type { UTCDate } from '@date-fns/utc';
import type { FindOperationsInput } from '@travel-planner/shared/schemas/operations';
import type { CardFilterContext, OperationsFilterCard } from '../model/cards';
import { useMemo } from 'react';
import { useFilterCardCounts } from '../api/use-filter-card-counts';
import { useMessagesFilterCardCounts } from '../api/use-messages-filter-card-counts';
import { messagesTabListFilter, overlayCardFilter } from '../model/cards';
import {
  formatCardQuery,
  formatMessagesCardQuery,
} from '../model/format-card-query';
import css from './filter-cards.module.scss';
import { FilterCountCard } from './filter-count-card';

type FilterCardsBarProps = {
  /** Cards to render (XT-380 per-tab set, or XT-225 dashboard-v2 set). */
  cards: OperationsFilterCard[];
  /** The list query WITHOUT any card overlay — each card counts base + its filter. */
  baseInput: FindOperationsInput;
  activeCardId?: string;
  /** Stable "now" (UTC) so fixed relative windows don't churn. */
  now: UTCDate;
  /** Org status ids for the dashboard-v2 Awaiting card. */
  awaitingStatusIds?: string[];
  /** Org-customisable guide languages for the Guides Languages card (XT-380). */
  guideLanguageExclude?: string[];
  /**
   * `operations` (default): `operations.countMany` overlaid on `baseInput`
   * (Home / Guides / Tickets / Transport, dashboard-v2).
   * `messages`: `externalMessages.findAll` countAll — same engine as the
   * Messages tab table so card number == list total.
   */
  countSource?: 'operations' | 'messages';
  onSelectCard: (id: string) => void;
};

export const FilterCardsBar = ({
  cards,
  baseInput,
  activeCardId,
  now,
  awaitingStatusIds,
  guideLanguageExclude,
  countSource = 'operations',
  onSelectCard,
}: FilterCardsBarProps) => {
  const ctx: CardFilterContext = useMemo(
    () => ({
      now,
      awaitingStatusIds,
      guideLanguageExclude,
    }),
    [now, awaitingStatusIds, guideLanguageExclude]
  );

  // One batch query for the whole row — same overlay as the drilled-in list.
  const resolvedQueries = useMemo(
    () => cards.map((card) => overlayCardFilter(baseInput, card.filter(ctx))),
    [cards, baseInput, ctx]
  );

  const opsCounts = useFilterCardCounts(
    countSource === 'operations' ? resolvedQueries : []
  );
  const messageCardIds = useMemo(
    () => (countSource === 'messages' ? cards.map((c) => c.id) : []),
    [countSource, cards]
  );
  const msgCounts = useMessagesFilterCardCounts(messageCardIds);

  const totals =
    countSource === 'messages' ? msgCounts.totals : opsCounts.data?.totals;

  if (cards.length === 0) return null;

  return (
    <div className={css.root}>
      {cards.map((card, index) => {
        const resolvedInput = resolvedQueries[index];
        const query =
          countSource === 'messages'
            ? formatMessagesCardQuery(messagesTabListFilter(card.id))
            : formatCardQuery(resolvedInput);
        return (
          <FilterCountCard
            active={activeCardId === card.id}
            agingHint={card.agingHint}
            count={totals?.[index]}
            description={card.description}
            dot={card.dot}
            key={card.id}
            label={card.label}
            onClick={() => onSelectCard(card.id)}
            query={query}
            subText={card.subText}
          />
        );
      })}
    </div>
  );
};
